import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, KeyRound, Copy, Trash2, X as XIcon, Clock, Users2, Eye, RefreshCw, Plus, MailCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, api, endpoints } from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONE = {
  "Active": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Revoked": "bg-rose-50 text-rose-700 border-rose-200",
  "Expired": "bg-slate-100 text-slate-600 border-slate-200",
};

const fmt = (s) => s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export default function AuditorAccessPage() {
  const [invites, setInvites] = useState([]);
  const [meta, setMeta] = useState({ areas: [] });
  const [clients, setClients] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    endpoints.listAuditorInvites().then(setInvites).catch(() => setInvites([]));
  };
  useEffect(() => {
    load();
    endpoints.auditorMetadata().then(setMeta).catch(() => setMeta({ areas: [] }));
    endpoints.listClients().then(setClients).catch(() => {});
  }, []);

  const stats = useMemo(() => ({
    total: invites.length,
    active: invites.filter(i => i.status === "Active").length,
    revoked: invites.filter(i => i.status === "Revoked").length,
    expired: invites.filter(i => i.status === "Expired").length,
  }), [invites]);

  const revoke = async (id) => {
    if (!confirm("Revoke access immediately? The auditor will be locked out.")) return;
    try { await endpoints.revokeAuditorInvite(id, {}); toast.success("Access revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this invite record and its audit log?")) return;
    try { await endpoints.deleteAuditorInvite(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const copyLink = (token) => {
    const url = `${window.location.origin}/auditor?token=${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Auditor link copied"));
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white flex items-center justify-center shadow-md">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Temporary Auditor Access</h1>
            <p className="text-xs text-slate-500">Invite external auditors to a specific client portal — read-only, time-boxed, revocable.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="auditor-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Button data-testid="auditor-new-invite" className="brand-gradient text-white" onClick={() => setOpenNew(true)}>
            <Plus className="h-4 w-4 mr-1" /> New auditor invite
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats.total} tone="indigo" />
        <Stat label="Active" value={stats.active} tone="emerald" />
        <Stat label="Revoked" value={stats.revoked} tone="rose" />
        <Stat label="Expired" value={stats.expired} tone="slate" />
      </div>

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 dark:bg-slate-900/40 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2.5 px-3">Auditor</th>
                  <th className="py-2.5 px-3">Client</th>
                  <th className="py-2.5 px-3">Window</th>
                  <th className="py-2.5 px-3">Areas</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Logins</th>
                  <th className="py-2.5 px-3">Last login</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-500">No auditor invites yet — send one to give a third-party auditor read-only access.</td></tr>
                )}
                {invites.map(inv => (
                  <tr key={inv.id} data-testid={`auditor-row-${inv.id}`} className="border-b hover:bg-slate-50/60 dark:hover:bg-white/5">
                    <td className="py-2.5 px-3">
                      <div className="font-medium">{inv.auditor_name}</div>
                      <div className="text-[11px] text-slate-500">{inv.auditor_email}</div>
                    </td>
                    <td className="py-2.5 px-3">{inv.client_name}</td>
                    <td className="py-2.5 px-3 text-xs">
                      <div>from {fmt(inv.start_at)}</div>
                      <div className="text-slate-500">until {fmt(inv.expires_at)}</div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {(inv.allowed_areas || []).map(a => <Badge key={a} variant="secondary" className="text-[10px]">{a}</Badge>)}
                      </div>
                    </td>
                    <td className="py-2.5 px-3"><Badge className={STATUS_TONE[inv.status]}>{inv.status}</Badge></td>
                    <td className="py-2.5 px-3 text-center font-mono">{inv.login_count || 0}</td>
                    <td className="py-2.5 px-3 text-xs">{fmt(inv.last_login_at)}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setDetail(inv)} data-testid={`auditor-view-${inv.id}`} title="View audit trail">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {inv.status === "Active" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => copyLink(inv.token)} data-testid={`auditor-copy-${inv.id}`} title="Copy auditor link">
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50" onClick={() => revoke(inv.id)} data-testid={`auditor-revoke-${inv.id}`} title="Revoke immediately">
                              <XIcon className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(inv.id)} data-testid={`auditor-delete-${inv.id}`} title="Delete record">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <NewInviteModal open={openNew} onOpenChange={setOpenNew} clients={clients} areas={meta.areas || []} onCreated={(inv) => { load(); toast.success(`Invite sent to ${inv.auditor_email}`); }} />
      <InviteDetailModal invite={detail} open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }} onCopy={copyLink} />
    </div>
  );
}

function Stat({ label, value, tone = "indigo" }) {
  const toneCls = { indigo: "text-indigo-600", emerald: "text-emerald-600", rose: "text-rose-600", slate: "text-slate-600" }[tone];
  return (
    <Card className="glass-card">
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`text-2xl font-extrabold mt-1 ${toneCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function NewInviteModal({ open, onOpenChange, clients, areas, onCreated }) {
  const now = new Date();
  const in7 = new Date(now.getTime() + 7 * 86400 * 1000);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [clientSlug, setClientSlug] = useState("");
  const [start, setStart] = useState(toLocal(now));
  const [expiry, setExpiry] = useState(toLocal(in7));
  const [selected, setSelected] = useState(["overview"]);
  const [packs, setPacks] = useState([]);
  const [packIds, setPackIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !clientSlug || !selected.includes("job_packs")) return;
    api.get("/job-packs", { params: { client_slug: clientSlug } }).then((r) => setPacks(r.data)).catch(() => setPacks([]));
  }, [open, clientSlug, selected]);
  useEffect(() => { setPackIds([]); }, [clientSlug]);
  const togglePack = (id) => setPackIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  useEffect(() => {
    if (open) {
      setName(""); setEmail(""); setClientSlug(clients?.[0]?.slug || "");
      setStart(toLocal(new Date())); setExpiry(toLocal(new Date(Date.now() + 7 * 86400 * 1000)));
      setSelected(["overview"]);
    }
  }, [open, clients]);

  const toggle = (a) => setSelected(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  const submit = async () => {
    if (!name.trim() || !email.trim() || !clientSlug) { toast.error("Fill in name, email and client"); return; }
    setBusy(true);
    try {
      const created = await endpoints.createAuditorInvite({
        auditor_name: name.trim(), auditor_email: email.trim().toLowerCase(),
        client_slug: clientSlug,
        start_at: new Date(start).toISOString(),
        expires_at: new Date(expiry).toISOString(),
        allowed_areas: selected, job_pack_ids: selected.includes("job_packs") ? packIds : [], created_by: "",
      });
      onCreated?.(created);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="auditor-new-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-indigo-600" /> Invite external auditor
          </DialogTitle>
          <DialogDescription>Access is read-only and time-boxed. The auditor receives a one-time link by email.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Auditor name</Label><Input data-testid="auditor-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Anne-Marie Blake" /></div>
            <div><Label className="text-xs mb-1 block">Auditor email</Label><Input data-testid="auditor-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="auditor@certbody.example" /></div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Client</Label>
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger data-testid="auditor-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
              <SelectContent>{(clients || []).map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Access start</Label><Input data-testid="auditor-start" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} /></div>
            <div><Label className="text-xs mb-1 block">Access expires</Label><Input data-testid="auditor-expiry" type="datetime-local" value={expiry} onChange={e => setExpiry(e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-xs mb-2 block">Allowed areas</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {areas.map(a => (
                <label key={a} data-testid={`auditor-area-${a}`} className="flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={selected.includes(a)} onCheckedChange={() => toggle(a)} />
                  <span className="text-sm capitalize">{a === "job_packs" ? "Audit prep job packs" : a}</span>
                </label>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Read-only by default. Auditor cannot navigate outside {areas.length ? "these" : "any"} areas or to any other client's portal.</div>
          </div>
          {selected.includes("job_packs") && (
            <div data-testid="auditor-job-packs">
              <Label className="text-xs mb-2 block">Job packs to share ({packIds.length} selected)</Label>
              {packs.length === 0 ? <p className="text-xs text-slate-500">This client has no job packs yet.</p> : (
                <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
                  {packs.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`auditor-pack-${p.id}`}>
                      <Checkbox checked={packIds.includes(p.id)} onCheckedChange={() => togglePack(p.id)} />
                      <span className="font-medium">{p.job_number}</span>
                      <span className="text-xs text-slate-500 truncate">{p.customer}</span>
                      <span className={`ml-auto text-[10px] ${p.complete ? "text-emerald-600" : "text-amber-600"}`}>{p.complete ? "Ready" : "Incomplete"}</span>
                    </label>
                  ))}
                </div>
              )}
              {packs.length > 0 && <button type="button" className="text-[11px] text-indigo-600 underline mt-1" onClick={() => setPackIds(packs.map((p) => p.id))} data-testid="auditor-packs-all">Select all</button>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button data-testid="auditor-submit" className="brand-gradient text-white" onClick={submit} disabled={busy}>
            <MailCheck className="h-4 w-4 mr-1" /> {busy ? "Sending..." : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDetailModal({ invite, open, onOpenChange, onCopy }) {
  if (!invite) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="auditor-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users2 className="h-4 w-4 text-indigo-600" /> {invite.auditor_name}</DialogTitle>
          <DialogDescription>{invite.auditor_email} · {invite.client_name}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <F l="Status"><Badge className={STATUS_TONE[invite.status]}>{invite.status}</Badge></F>
            <F l="Logins">{invite.login_count || 0}</F>
            <F l="Start">{fmt(invite.start_at)}</F>
            <F l="Expires">{fmt(invite.expires_at)}</F>
          </div>
          <F l="Allowed areas"><div className="flex flex-wrap gap-1">{(invite.allowed_areas || []).map(a => <Badge key={a} variant="secondary">{a}</Badge>)}</div></F>
          {invite.status === "Active" && (
            <F l="Invite link">
              <div className="flex items-center gap-2">
                <code className="text-[11px] flex-1 truncate rounded bg-slate-50 border px-2 py-1">{window.location.origin}/auditor?token={invite.token?.slice(0, 12)}…</code>
                <Button size="sm" variant="outline" onClick={() => onCopy(invite.token)}><Copy className="h-4 w-4" /></Button>
              </div>
            </F>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Audit trail</div>
            <ol className="relative border-l-2 border-slate-200 dark:border-white/10 ml-2 pl-3 space-y-2 max-h-80 overflow-y-auto" data-testid="auditor-audit-trail">
              {(invite.access_log || []).length === 0 && <li className="text-xs text-slate-400">No logins or activity yet.</li>}
              {(invite.access_log || []).slice().reverse().map(h => (
                <li key={h.id} className="text-xs">
                  <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-indigo-500 border-2 border-white dark:border-slate-900"></span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold capitalize">{(h.action || "").replace(/_/g, " ")}</span>
                    {h.area && <Badge variant="secondary" className="text-[10px]">{h.area}</Badge>}
                    <span className="text-slate-400">· {fmt(h.at)}</span>
                  </div>
                  {h.details && <div className="text-slate-600 dark:text-slate-300">{h.details}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function F({ l, children }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{l}</div><div className="text-sm mt-1">{children}</div></div>;
}
